import { PlugIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { CopyButton } from "./ui/copy-button";

interface McpAccordionProps {
  id: string | number;
  title: string;
  serverLabel?: string;
  defaultOpen?: boolean;
  className?: string;
  children: React.ReactNode;
  copyValue?: string;
  /** Controls the streaming pulse animation on the icon */
  isLive?: boolean;
  variant?: "default" | "error";
}

const McpAccordion: React.FC<McpAccordionProps> = ({
  id,
  title,
  serverLabel,
  defaultOpen = false,
  className = "",
  children,
  copyValue,
  isLive = false,
  variant = "default",
}) => {
  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    defaultOpen ? `item-${id}` : undefined,
  );

  useEffect(() => {
    if (defaultOpen) {
      setAccordionValue(`item-${id}`);
    }
  }, [defaultOpen, id]);

  const isFirstRenderRef = useRef(true);
  const prevChildrenRef = useRef(children);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevChildrenRef.current = children;
      return;
    }
    if (isLive && children !== prevChildrenRef.current) {
      setAccordionValue(`item-${id}`);
    }
    prevChildrenRef.current = children;
  }, [children, id, isLive]);

  const isError = variant === "error";
  const borderColor = isError ? "border-red-200" : "border-blue-200";
  const hoverBg = isError ? "hover:bg-red-50/60" : "hover:bg-blue-50/60";
  const openBorderColor = isError
    ? "data-[state=open]:border-red-200"
    : "data-[state=open]:border-blue-200";
  const contentBg = isError ? "bg-red-50/20" : "bg-blue-50/20";
  const iconColor = isError ? "text-red-500" : "text-blue-500";

  return (
    <div className={`mb-3 w-full rounded-lg border ${borderColor} bg-white ${className}`}>
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value={`item-${id}`} className="border-0">
          <AccordionTrigger
            className={`group overflow-hidden rounded-t-lg px-4 py-3 text-left text-sm font-medium text-gray-700 transition-all duration-200 ${hoverBg} hover:no-underline data-[state=open]:rounded-none data-[state=open]:border-b ${openBorderColor}`}
          >
            <div className="flex items-center gap-2">
              <div className="relative flex items-center justify-center">
                <PlugIcon className={`h-3.5 w-3.5 ${iconColor}`} />
                {isLive && (
                  <div className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                )}
              </div>
              <span className="text-sm font-medium">{title}</span>
              {serverLabel && (
                <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                  {serverLabel}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="group p-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className={`relative rounded-b-lg ${contentBg}`}>
              {children}
              {copyValue && (
                <div className="absolute right-3 top-3 z-10">
                  <CopyButton
                    text={copyValue}
                    className="bg-white/70 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    title="Copy"
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default McpAccordion;
